import { useCallback } from 'react';

import { usePathname } from 'expo-router';
import { useSelectStore } from 'features/store/hooks/use-select-store';

import { IRestaurant } from '@rbi-ctg/store';
import { useDismissAndReplace } from 'navigation/hooks/use-dismiss-and-navigate';
import { ServiceMode } from 'state/order';
import { useServiceModeContext } from 'state/service-mode';
import { useUIContext } from 'state/ui';
import { routes } from 'utils/routing';

import { useSavedRecommendedStores } from './use-saved-recommended-stores';

export const useDialogActions = ({
  availableRestaurant,
  clearAllSearchState,
}: {
  availableRestaurant?: IRestaurant;
  clearAllSearchState?: () => void;
}) => {
  const {
    setIsStoreOrderUnavailableDialogOpen,
    isStoreOrderUnavailableDialogOpen,
  } = useUIContext();
  const { setAlreadySelectedRecommendedStore } = useSavedRecommendedStores();
  const selectStore = useSelectStore();
  const { serviceMode } = useServiceModeContext();
  const dismissAndReplace = useDismissAndReplace();
  const pathname = usePathname();

  const selectNearbyStore = async () => {
    if (!availableRestaurant?._id) {
      return;
    }
    await selectStore({
      newStore: availableRestaurant,
      callback: () => {
        handleDialogClose();
      },
      requestedServiceMode: serviceMode ?? ServiceMode.DRIVE_THRU,
      skipRedirection: true,
      skipChangeStoreMessage: true,
    });
    setAlreadySelectedRecommendedStore(availableRestaurant._id);
  };

  const chooseAnotherStore = () => {
    handleDialogClose();
    dismissAndReplace({
      pathname: routes.storeLocator,
      params: {
        redirectTo: pathname,
      },
    });
  };

  const handleDialogClose = useCallback(() => {
    setIsStoreOrderUnavailableDialogOpen(false);
    clearAllSearchState?.();
  }, [clearAllSearchState, setIsStoreOrderUnavailableDialogOpen]);

  return {
    isDialogOpen: isStoreOrderUnavailableDialogOpen,
    setIsDialogOpen: setIsStoreOrderUnavailableDialogOpen,
    availableRestaurant,
    selectNearbyStore,
    chooseAnotherStore,
    handleDialogClose,
  };
};
